<script setup lang="ts">
import { shallowRef, ref, onMounted, onUnmounted, markRaw, watch } from 'vue'
import { useRenderLoop, useTresContext } from '@tresjs/core'
import {
  BufferGeometry,
  BufferAttribute,
  ShaderMaterial,
  Points,
  Color,
  AdditiveBlending,
  Vector3,
} from 'three'
import { usePerformanceMode } from '@/composables/usePerformanceMode'

const { camera } = useTresContext()
const { isLowPower } = usePerformanceMode()

const isMobile = ref(false)
const pixelRatio = ref(1)

let onResize: (() => void) | null = null

const computeIsMobile = () => (typeof window !== 'undefined' ? window.innerWidth < 1024 : false)
const computePixelRatio = () => {
  if (typeof window === 'undefined') return 1
  const cap = isLowPower.value ? 1 : 1.5
  const ratio = window.devicePixelRatio || 1
  return Math.min(ratio, cap)
}

onMounted(() => {
  isMobile.value = computeIsMobile()
  pixelRatio.value = computePixelRatio()

  onResize = () => {
    isMobile.value = computeIsMobile()
    pixelRatio.value = computePixelRatio()
    material.uniforms.uPixelRatio.value = pixelRatio.value
  }

  window.addEventListener('resize', onResize, { passive: true })
})

onUnmounted(() => {
  if (onResize) window.removeEventListener('resize', onResize)
})

// Props utilisateur
const props = defineProps({
  starsCount: { type: Number, default: 1000 },
  size: { type: Number, default: 30 },
  speed: { type: Number, default: 0.5 },
  sizes: { type: Object, default: () => ({ x: 10, y: 10, z: 10 }) },
  hoverStrength: { type: Number, default: 3.0 },
  opacity: { type: Number, default: 1.0 },
  clusterCount: { type: Number, default: 20 },
  clusterSize: { type: Number, default: 4.0 },
  disabled: { type: Boolean, default: false },
})

// === Shaders ===
const vertexShader = /* glsl */ `
attribute float aScale;
attribute vec3 aColor;
varying vec3 vColor;

uniform float uPixelRatio;
uniform float uTime;
uniform float uSize;
uniform float uSpeed;
uniform vec3 uMouse;
uniform float uHoverStrength;
uniform bool uIsMobile; // New uniform for mobile check

void main() {
  vColor = aColor;
  vec4 modelPosition = modelMatrix * vec4(position, 1.0);

  vec4 viewPosition = viewMatrix * modelPosition;

  // Influence de la souris avec atténuation selon profondeur
  float influence = 0.0;
  if (uIsMobile == false) {
    float distToMouse = distance(modelPosition.xyz, uMouse);
    influence = clamp(1.0 - distToMouse / uHoverStrength, 0.0, 1.0);

    // ⚠️ on atténue l’influence avec la profondeur du point
    float depthFactor = clamp(1.0 / (-viewPosition.z + 1.0), 0.35, 1.0);
    influence *= depthFactor;
  }

  // Use a power function to create more variation in star sizes
  float size = pow(aScale, 80.0) * 2.5 + influence * 1.5 + 0.15;

  vec4 projectedPosition = projectionMatrix * viewPosition;
  gl_Position = projectedPosition;
  gl_PointSize = uSize * size * uPixelRatio;
  gl_PointSize *= (1.0 / -viewPosition.z);
}
`

const fragmentShader = /* glsl */ `
varying vec3 vColor;
uniform float uOpacity;
uniform bool uIsMobile; // New uniform for mobile check

void main() {
  float distanceToCenter = distance(gl_PointCoord, vec2(0.5));

  // Use a power function for a smoother, more controlled falloff
  float strength = pow(1.0 - distanceToCenter * 2.0, 2.0);

  // Mix with white to make colors paler
  vec3 finalColor = mix(vColor, vec3(1.0), 0.5);

  gl_FragColor = vec4(finalColor, uIsMobile ? 1.0 : strength * uOpacity);
}
`

// === Géométrie
const geometry = new BufferGeometry()
const positions = new Float32Array(props.starsCount * 3)
const aScale = new Float32Array(props.starsCount)
const colors = new Float32Array(props.starsCount * 3)

const starColors = [
  new Color('#5588ff'), // Bright Blue
  new Color('#88aaff'), // Light Blue
  new Color('#aaccff'), // Very Light Blue
  new Color('#ffffff'), // White
  new Color('#d1c4e9'), // Light Purple
  new Color('#b39ddb')  // Soft Purple
];

// Create cluster centers
const clusters = [];
const yStep = props.sizes.y / props.clusterCount;
const yStart = -props.sizes.y / 2;

for (let i = 0; i < props.clusterCount; i++) {
  clusters.push({
    x: (Math.random() - 0.5) * props.sizes.x,
    y: yStart + i * yStep + (Math.random() - 0.5) * yStep * 0.5,
  });
}

for (let i = 0; i < props.starsCount; i++) {
  const cluster = clusters[Math.floor(Math.random() * props.clusterCount)];
  
  // Create a point in a sphere around the cluster center for X and Y
  const clusterRadius = Math.random() * props.clusterSize;
  const clusterAngle = Math.random() * 2 * Math.PI;

  positions[i * 3 + 0] = cluster.x + (clusterRadius * Math.random()) * Math.cos(clusterAngle);
  positions[i * 3 + 1] = cluster.y + (clusterRadius * Math.random()) * Math.sin(clusterAngle);

  // --- New Z-depth distribution logic ---
  let starZ;
  const depthRandom = Math.random();

  if (depthRandom < 0.01) { // 1% foreground
    starZ = -0.5 - Math.random(); // Between -0.5 and -1.5
  } else if (depthRandom < 0.01 + 0.95) { // 95% main
    starZ = -2.5 - Math.random(); // Between -2.5 and -3.5
  } else { // 4% background
    starZ = -8 - Math.random() * 4; // Between -8 and -12
  }

  // Add a small random offset to the Z position for organic feel
  positions[i * 3 + 2] = starZ + (Math.random() - 0.5) * 0.5;

  aScale[i] = Math.pow(Math.random(), 3.0);

  // Assign a color with a bias towards blue
  let color;
  const random = Math.random();
  if (random < 0.6) { // 60% chance for blue shades
    color = starColors[Math.floor(Math.random() * 3)];
  } else if (random < 0.8) { // 20% chance for white
    color = starColors[3];
  } else { // 20% chance for purple shades
    color = starColors[4 + Math.floor(Math.random() * 2)];
  }

  colors[i * 3 + 0] = color.r;
  colors[i * 3 + 1] = color.g;
  colors[i * 3 + 2] = color.b;
}

geometry.setAttribute('position', new BufferAttribute(positions, 3))
geometry.setAttribute('aScale', new BufferAttribute(aScale, 1))
geometry.setAttribute('aColor', new BufferAttribute(colors, 3));

// === Material
const material = new ShaderMaterial({
  vertexShader,
  fragmentShader,
  transparent: true,
  depthWrite: false,
  blending: AdditiveBlending,
  uniforms: {
    uSize: { value: props.size },
    uTime: { value: 0 },
    uPixelRatio: { value: pixelRatio.value },
    uSpeed: { value: props.speed },
    uMouse: { value: new Vector3() },
    uHoverStrength: { value: 0 },
    uOpacity: { value: props.opacity },
    uIsMobile: { value: isMobile.value }, // Pass isMobile to shader
  },
})

// === Points object (avoid Vue proxy)
const pointsObject = shallowRef(null)
pointsObject.value = markRaw(new Points(geometry, material))

// === Animation
const { onLoop } = useRenderLoop()

let unsubscribeLoop = null
unsubscribeLoop = onLoop(({ elapsed }) => {
  if (props.disabled) {
    return
  }
  material.uniforms.uTime.value = elapsed * 0.001
})

onMounted(() => {
  material.uniforms.uIsMobile.value = isMobile.value
  material.uniforms.uPixelRatio.value = pixelRatio.value
})

watch(isMobile, (value) => {
  material.uniforms.uIsMobile.value = value
})

watch(isLowPower, () => {
  pixelRatio.value = computePixelRatio()
  material.uniforms.uPixelRatio.value = pixelRatio.value
})

watch(() => props.opacity, (value) => {
  material.uniforms.uOpacity.value = props.disabled ? 0 : value
})

watch(() => props.disabled, (value) => {
  material.uniforms.uOpacity.value = value ? 0 : props.opacity
  if (value) {
    material.uniforms.uMouse.value.set(0, 0, 0)
  }
})

onUnmounted(() => {
  if (typeof unsubscribeLoop === 'function') {
    unsubscribeLoop()
  } else if (unsubscribeLoop && typeof unsubscribeLoop.stop === 'function') {
    unsubscribeLoop.stop()
  }
  pointsObject.value = null
  // Dispose GPU resources
  geometry.dispose()
  material.dispose()
})
</script>

<template>
  <TresGroup :position="[0, -props.sizes.y / 2 + 1.6, 0 ]">
    <primitive :object="pointsObject" />
  </TresGroup>
  
</template>
